import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import HotelHitOrigo from './hotelhit/HotelHitOrigo';
import getHotelUrl from '../utils/getHotelUrl';
import { usePax } from '../hooks/usePax';
import { useInitialState } from '../hooks/useInitialState';
import { useDepartures } from '../hooks/useDepartures';
import { useMucd } from '../hooks/useMucd';
import { logToDataLayer } from '../hooks/useAnalytics';
import { toSiteId } from '../../server/util/idToMucd';
import { useUmbracoDurationOptions } from '../hooks/useUmbracoTexts';

const getPaxString = (pax, texts) => {
  if (pax) {
    const [nAdults, nChildren] = pax.split('_').map((n) => parseInt(n, 10));

    const str = `${nAdults} ${nAdults > 1 ? texts.adults : texts.adult}`;
    if (!nChildren) {
      return str;
    }

    return `${str}, ${nChildren} ${nChildren > 1 ? texts.children : texts.child}`;
  }

  return null;
};

const HotelContainer = (props) => {
  const { hotelObject, location, onClose, openPriceDetails, position } = props;

  const mucd = useMucd();

  const { meta, umbracoTexts, travelType, featureToggles } = useInitialState();

  const durationOptions = useUmbracoDurationOptions();

  const { links: umbracoLinks } = umbracoTexts;

  const { selectedDeparturesOption: selectedDeparture } = useDepartures(location.searchParams);

  const { pax, roomAges: paxAges = [] } = usePax(location.searchParams);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (parseInt(params.get('details'), 10) === hotelObject.wvId) {
      openPriceDetails(hotelObject);
    }
  }, [hotelObject.wvId, openPriceDetails, hotelObject]);

  if (!hotelObject) {
    return null;
  }

  const charterHotelCode =
    hotelObject?.hotel?.__meta?.hotelCode ||
    hotelObject?.hotel?.content?.parts?.[0]?.codes?.[0]?.hotCd;

  const impression = {
    name: [
      hotelObject?.hotel?.__meta?.departureCode || selectedDeparture?.[0]?.key,
      hotelObject?.hotel?.__meta?.destinationCode || meta?.destinationCode,
    ]
      .filter((x) => x)
      .join(' - '),
    id: charterHotelCode
      ? [hotelObject?.hotel?.__meta?.destinationCode || meta?.destinationCode, charterHotelCode]
          .filter((x) => x)
          .join('')
      : hotelObject?.hotel?.content?.parts?.[0]?.codes?.[0]?.code,
    price: hotelObject?.hotel?.priceOffer?.totalPrice || '',
    brand: hotelObject?.hotel?.mainCategory?.name || 'Other',
    category: travelType,
    variant: 'VITS',
    list: 'Hotel Finder List',
    position,
  };
  let hotelUrl = hotelObject?.hotel?.content?.url;

  if (hotelObject?.hotel?.priceOffer) {
    hotelUrl = getHotelUrl(
      hotelObject,
      umbracoLinks,
      location.searchParams,
      mucd,
      selectedDeparture,
      paxAges.toString(),
    );
  } else {
    const isIndependent = !!hotelObject?.isIndependent;
    const { QueryDepID, QueryRoomAges } = location.searchParams;

    let { QueryDur } = location.searchParams;
    if (!QueryDur) QueryDur = isIndependent ? 7 : durationOptions[0]?.value;

    const queryAges = QueryRoomAges?.replace('|', '');
    const queryArray = [
      `QueryDepID=${QueryDepID || selectedDeparture?.[0]?.caId}`,
      `QueryDur=${QueryDur}`,
    ];
    if (queryAges)
      queryArray.push(isIndependent ? `QueryRoomAges=${queryAges}` : `QueryAges=${queryAges}`);
    if (isIndependent)
      queryArray.push(...['QueryUnits=1', 'QueryType=IndependentPackage', 'qf=flowDynamic']);

    hotelUrl = `${hotelObject?.hotel?.content?.url}?${queryArray.join('&')}`;
  }

  const showPriceDetailsLink = () => {
    if (hotelObject?.isIndependent) return false;

    const isVingSe = toSiteId(mucd) === 1;
    return isVingSe ? featureToggles?.showPricedetails : true;
  };

  return (
    <HotelHitOrigo
      hotelUrl={hotelUrl}
      isMapView={location.searchParams?.view === 'map'}
      onClose={onClose}
      onSelect={() =>
        logToDataLayer({
          event: 'productClick',
          ecommerce: {
            click: { actionField: { list: 'Hotel Finder List' }, products: [impression] },
          },
        })
      }
      openPriceDetails={showPriceDetailsLink() ? () => openPriceDetails(hotelObject) : false}
      origoData={hotelObject.hotel}
      paxLabel={getPaxString(pax, umbracoTexts?.labels)}
      webuiHotelHitTexts={umbracoTexts?.webuiHotelHitTexts}
    />
  );
};

export const fragment = gql`
  fragment HotelContainerHotel on Hotel {
    content {
      caId
      parts {
        codes {
          hotCd
          code
        }
      }
      geographical {
        country {
          caId
        }
        resort {
          caId
        }
      }
    }
  }
`;

export default HotelContainer;
